/**
 * Tool Detail Page
 * Detail page for viewing embedded tool content with progress tracking
 */

import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ResourceViewer,
  BookmarkButton,
  ProgressIndicator,
} from "@components/library";
import {
  ToolCard,
  PricingBadge,
  VendorBadge,
  IntegrationTypeBadge,
  ReviewList,
} from "@components/tools";
import {
  useLibraryResource,
  useUpdateProgress,
  useLibraryResources,
} from "@hooks/useLibrary";
import {
  useTrackToolView,
  useToolReviews,
  useMyReview,
  useRatingSummary,
  useCreateReview,
  useUpdateReview,
  useDeleteReview,
} from "@hooks/useTools";
import { cn } from "@utils/cn";
import type {
  LibraryDifficulty,
  LibraryResourceWithUserData,
} from "@/types/models";
import type { Tool } from "@/types/tools";

// Difficulty badge colors
function getDifficultyStyles(difficulty: LibraryDifficulty) {
  switch (difficulty) {
    case "beginner":
      return "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400";
    case "intermediate":
      return "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400";
    case "advanced":
      return "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400";
  }
}

// Format topic for display
function formatTopic(topic: string): string {
  return topic
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// Type guard to check if tool has marketplace data
function hasToolData(
  resource: LibraryResourceWithUserData,
): resource is LibraryResourceWithUserData & Partial<Tool> {
  return (
    "pricingTier" in resource ||
    "vendor" in resource ||
    "integrationType" in resource
  );
}

export function ToolDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [reviewPage, setReviewPage] = useState(1);

  // Fetch the resource from API
  const { data: tool, isLoading, error } = useLibraryResource(id || "");

  // Progress mutation
  const { mutate: updateProgress } = useUpdateProgress();

  // View tracking mutation
  const { mutate: trackView } = useTrackToolView();
  const hasTrackedView = useRef(false);

  // Fetch all tools for related tools
  const { data: allTools = [] } = useLibraryResources({ type: "tool" });

  // Review hooks
  const { data: ratingSummary, isLoading: isLoadingRating } = useRatingSummary(
    id || "",
  );
  const { data: reviewsData, isLoading: isLoadingReviews } = useToolReviews(
    id || "",
    reviewPage,
  );
  const { data: myReview, isLoading: isLoadingMyReview } = useMyReview(
    id || "",
  );
  const createReviewMutation = useCreateReview(id || "");
  const updateReviewMutation = useUpdateReview(id || "");
  const deleteReviewMutation = useDeleteReview(id || "");

  // Auto-mark as in_progress when viewing
  useEffect(() => {
    if (tool && tool.progressStatus === "not_started") {
      updateProgress({ resourceId: tool.id, status: "in_progress" });
    }
  }, [tool, updateProgress]);

  // Track view on first load
  useEffect(() => {
    if (tool && !hasTrackedView.current) {
      trackView(tool.id);
      hasTrackedView.current = true;
    }
  }, [tool, trackView]);

  // Get tool-specific data if available
  const toolData = tool && hasToolData(tool) ? tool : null;

  // Get related tools (same topic, different tool)
  const relatedTools = allTools
    .filter(
      (t: LibraryResourceWithUserData) =>
        t.id !== tool?.id &&
        t.topics.some((topic) => tool?.topics.includes(topic)),
    )
    .slice(0, 3);

  // Loading state
  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3">
            <div className="h-8 w-64 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-4" />
            <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
            <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-8" />
            <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-16">
          <svg
            className="w-20 h-20 mx-auto text-red-400 dark:text-red-500 mb-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Failed to Load Tool
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error instanceof Error
              ? error.message
              : "An unexpected error occurred"}
          </p>
          <Link
            to="/tools"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Tools
          </Link>
        </div>
      </div>
    );
  }

  // Tool not found
  if (!tool) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-16">
          <svg
            className="w-20 h-20 mx-auto text-gray-400 dark:text-gray-600 mb-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Tool Not Found
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The tool you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to="/tools"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Tools
          </Link>
        </div>
      </div>
    );
  }

  // Handle mark as complete
  const handleMarkComplete = () => {
    updateProgress({ resourceId: tool.id, status: "completed" });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <nav className="mb-6">
        <ol className="flex items-center gap-2 text-sm">
          <li>
            <Link
              to="/tools"
              className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Tools
            </Link>
          </li>
          <li className="text-gray-400 dark:text-gray-600">/</li>
          <li className="text-gray-900 dark:text-white font-medium truncate max-w-xs">
            {tool.title}
          </li>
        </ol>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-3">
          {/* Tool Header */}
          <div className="mb-6">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {/* Progress status */}
              <ProgressIndicator status={tool.progressStatus} size="sm" />
              {/* Pricing tier */}
              {toolData?.pricingTier && (
                <PricingBadge tier={toolData.pricingTier} size="md" />
              )}
              {/* Integration type */}
              {toolData?.integrationType && (
                <IntegrationTypeBadge
                  type={toolData.integrationType}
                  size="md"
                />
              )}
              <span
                className={cn(
                  "px-2 py-1 text-xs font-medium rounded-full capitalize",
                  getDifficultyStyles(tool.difficulty),
                )}
              >
                {tool.difficulty}
              </span>
              {tool.topics.map((topic) => (
                <span
                  key={topic}
                  className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded"
                >
                  {formatTopic(topic)}
                </span>
              ))}
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                {/* Tool icon */}
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-50 dark:from-primary-900/40 dark:to-primary-800/20 rounded-xl flex items-center justify-center shadow-sm">
                  <svg
                    className="w-6 h-6 text-primary-600 dark:text-primary-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z"
                    />
                  </svg>
                </div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  {tool.title}
                </h1>
              </div>
              <BookmarkButton
                resourceId={tool.id}
                isBookmarked={tool.isBookmarked}
                size="lg"
                showLabel
              />
            </div>

            <p className="text-lg text-gray-600 dark:text-gray-400 mb-4 mt-3">
              {tool.description}
            </p>

            {/* Meta info */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
              {tool.author && (
                <span className="flex items-center gap-1">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  {tool.author}
                </span>
              )}
              {tool.estimatedMinutes && (
                <span className="flex items-center gap-1">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {tool.estimatedMinutes} min read
                </span>
              )}
            </div>
          </div>

          {/* Content Viewer or External Link */}
          {tool.source === "external" && tool.externalUrl ? (
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-xl p-8 text-center border border-primary-200 dark:border-primary-700">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary-100 dark:bg-primary-800/40 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-primary-600 dark:text-primary-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                External Tool
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                This tool is hosted externally. Click the button below to visit the tool's website and learn more.
              </p>
              <a
                href={tool.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                Visit Website
              </a>
            </div>
          ) : (
            <ResourceViewer resource={tool} />
          )}

          {/* Mark as Complete button */}
          {tool.progressStatus !== "completed" && (
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleMarkComplete}
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Mark as Complete
              </button>
            </div>
          )}

          {/* Completed message */}
          {tool.progressStatus === "completed" && (
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                You've completed this tool
              </div>
            </div>
          )}

          {/* Reviews Section */}
          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
            <ReviewList
              toolId={tool.id}
              ratingSummary={ratingSummary}
              reviews={reviewsData}
              myReview={myReview}
              isLoading={isLoadingRating || isLoadingMyReview}
              isLoadingReviews={isLoadingReviews}
              onCreateReview={(data) => createReviewMutation.mutate(data)}
              onUpdateReview={(data) => updateReviewMutation.mutate(data)}
              onDeleteReview={() => deleteReviewMutation.mutate()}
              onPageChange={setReviewPage}
              currentPage={reviewPage}
              isCreating={createReviewMutation.isPending}
              isUpdating={updateReviewMutation.isPending}
              isDeleting={deleteReviewMutation.isPending}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          {/* Back to Tools */}
          <div className="mb-6">
            <Link
              to="/tools"
              className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to Tools
            </Link>
          </div>

          {/* Vendor Info */}
          {toolData?.vendor && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Vendor
              </h2>
              <VendorBadge vendor={toolData.vendor} size="md" />
              {toolData.vendor.website && (
                <a
                  href={toolData.vendor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                >
                  Visit Website
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
              )}
            </div>
          )}

          {/* External Links */}
          {(toolData?.documentationUrl ||
            toolData?.githubUrl ||
            toolData?.demoVideoUrl) && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                Resources
              </h2>
              <div className="space-y-2">
                {toolData.documentationUrl && (
                  <a
                    href={toolData.documentationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    </svg>
                    Documentation
                  </a>
                )}
                {toolData.githubUrl && (
                  <a
                    href={toolData.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                      />
                    </svg>
                    GitHub Repository
                  </a>
                )}
                {toolData.demoVideoUrl && (
                  <a
                    href={toolData.demoVideoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    Demo Video
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Pricing Details */}
          {toolData?.pricingDetails && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Pricing Details
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {toolData.pricingDetails}
              </p>
            </div>
          )}

          {/* Related Tools */}
          {relatedTools.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Related Tools
              </h2>
              <div className="space-y-4">
                {relatedTools.map((related: LibraryResourceWithUserData) => (
                  <ToolCard key={related.id} tool={related} showUserData />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
